import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import api from 'js/api-helper';

import Button from 'components/button/Button';
import Link from 'components/link/Link';
import TextInput from 'components/form-elements/text-input/TextInput';
import SearchSuggestions from 'components/search-suggestions/SearchSuggestions';

import { ISearch } from 'source/NFR.types';

interface ISearchProps extends ISearch {
  theme?: string;
}

const Search: React.FC<ISearchProps> = ({
  externalResultsLabel,
  externalResultsEndpoint,
  input,
  resultsDescription,
  submit,
  searchSuggestions,
  theme
}) => {
  const inputElement = useRef<HTMLInputElement>(null);
  const [externalResultsCount, setExternalResultsCount] = useState(0);
  const [externalResultsUrl, setExternalResultsUrl] = useState('');
  const [inputValue, setInputValue] = useState(input.value || '');

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const removeText = () => {
    inputElement.current?.focus();
    setInputValue('');
  };

  useEffect(() => {
    if (externalResultsEndpoint) {
      void api
        .get<{ Hits: number; url: string }>(externalResultsEndpoint)
        .then(({ Hits, url }) => {
          setExternalResultsCount(Hits);
          setExternalResultsUrl(url);
        })
        .catch(() => {
          // Graceful degradation: external results section won't show
        });
    }
  }, [externalResultsEndpoint]);

  useEffect(() => {
    // On every new input response from backend:
    if (typeof input.value !== 'string') return;
    setInputValue(input.value);
  }, [input.dataId, input.value]);

  return (
    <div className={cn('search', theme)}>
      <div className="search--input-wrapper">
        <div className="search--field">
          <TextInput
            {...input}
            className="search--input"
            type="search"
            icon="search"
            onChange={onInputChange}
            ref={inputElement}
            theme={['rounded', 'hiddenLabel']}
            controlled
            value={inputValue.toString()}
          />
          {!!inputValue.toString().length && <Button className="search--clear" icon="x" onClick={removeText} />}
        </div>

        <Button className="search--submit" type="submit" theme={['primary', 'large']} {...submit} />
      </div>

      <div className="search--results">
        {resultsDescription}
        {externalResultsUrl && (
          <Link
            icon="external-link-arrow"
            iconClassName="search--results-icon"
            url={externalResultsUrl}
            text={`${externalResultsCount} ${externalResultsLabel}`}
          />
        )}
        {searchSuggestions && <SearchSuggestions {...searchSuggestions} />}
      </div>
    </div>
  );
};

Search.displayName = 'Search';

export default Search;
